import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { monitoringAPI, activityLogsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * 專案監控 Tab - 表格形式
 * 顯示同步紀錄、提醒紀錄、操作紀錄
 */
export default function ProjectMonitoringTab({ projectId }) {
  const [activeTab, setActiveTab] = useState('sync');
  const { getAuthHeader } = useAuth();

  // 分頁狀態
  const [syncPage, setSyncPage] = useState(0);
  const [reminderPage, setReminderPage] = useState(0);
  const [activityPage, setActivityPage] = useState(0);
  const pageSize = 20;

  // 取得同步歷史
  const { data: syncData, isLoading: syncLoading } = useQuery({
    queryKey: ['monitoring', 'sync-history', projectId, syncPage],
    queryFn: () => monitoringAPI.getSyncHistory({
      project_id: projectId,
      limit: pageSize,
      offset: syncPage * pageSize
    }),
    staleTime: 60 * 1000,
    enabled: activeTab === 'sync',
  });

  // 取得提醒紀錄
  const { data: reminderData, isLoading: reminderLoading } = useQuery({
    queryKey: ['monitoring', 'reminder-logs', projectId, reminderPage],
    queryFn: () => monitoringAPI.getReminderLogs({
      project_id: projectId,
      limit: pageSize,
      offset: reminderPage * pageSize
    }),
    staleTime: 60 * 1000,
    enabled: activeTab === 'reminders',
  });

  // 取得專案操作紀錄
  const { data: activityData, isLoading: activityLoading } = useQuery({
    queryKey: ['activity-logs', projectId, activityPage],
    queryFn: async () => {
      const headers = await getAuthHeader();
      return activityLogsAPI.getByProject(projectId, {
        limit: pageSize,
        offset: activityPage * pageSize
      }, headers);
    },
    staleTime: 60 * 1000,
    enabled: activeTab === 'activity',
  });

  const syncLogs = syncData?.data || [];
  const syncPagination = syncData?.pagination || { total: 0, hasMore: false };
  const reminderLogs = reminderData?.data || [];
  const reminderPagination = reminderData?.pagination || { total: 0, hasMore: false };
  const activityLogs = activityData?.data?.logs || [];
  const activityPagination = activityData?.data?.pagination || { total: 0, has_more: false };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status, success) => {
    if (success === true || status === 'success' || status === 'sent') {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">成功</span>;
    }
    if (status === 'rejected') {
      return <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">提交被拒</span>;
    }
    if (status === 'bounced') {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">退信 ❌</span>;
    }
    if (success === false || status === 'error' || status === 'failed') {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">失敗</span>;
    }
    if (status === 'skipped') {
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">跳過</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">{status || '-'}</span>;
  };

  const getReminderTypeBadge = (type, days) => {
    switch (type) {
      case 'periodic':
        return <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">定期提醒</span>;
      case 'deadline_upcoming':
        return (
          <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
            截止日將近 {days !== undefined && `(剩 ${days} 天)`}
          </span>
        );
      case 'deadline_today':
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">截止日當天</span>;
      case 'deadline_check':
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">檢查</span>;
      default:
        return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">提醒</span>;
    }
  };

  const getActivityTypeBadge = (type, action) => {
    const typeMap = {
      'reminder_configured': { label: '設定提醒', style: 'bg-yellow-100 text-yellow-800' },
      'reminder_removed': { label: '提醒移除', style: 'bg-yellow-100 text-yellow-800' },
      'reminder': { label: '提醒', style: 'bg-yellow-100 text-yellow-800' },
      'forms_assigned': { label: '表單指派', style: 'bg-pink-100 text-pink-800' },
      'vendors_assigned': { label: '廠商指派', style: 'bg-pink-100 text-pink-800' },
      'form_vendor_assigned': { label: '表單廠商指派', style: 'bg-pink-100 text-pink-800' },
      'assignment_removed': { label: '指派移除', style: 'bg-red-100 text-red-800' },
      'assignment_updated': { label: '指派更新', style: 'bg-pink-100 text-pink-800' },
      'sync': { label: '同步', style: 'bg-indigo-100 text-indigo-800' },
      'form_created': { label: '問卷建立', style: 'bg-cyan-100 text-cyan-800' },
      'form': { label: '問卷', style: 'bg-cyan-100 text-cyan-800' },
      'vendor': { label: '供應商', style: 'bg-orange-100 text-orange-800' },
      'response': { label: '回覆', style: 'bg-green-100 text-green-800' },
      'response_resend': { label: '要求重填', style: 'bg-blue-100 text-blue-800' },
      'response_deleted': { label: '回覆刪除', style: 'bg-red-100 text-red-800' },
      'project': { label: '專案', style: 'bg-purple-100 text-purple-800' },
      'permission': { label: '權限', style: 'bg-red-100 text-red-800' },
    };

    // 特殊處理 sync 類型的 action
    if (type === 'sync') {
      if (action === 'sheet_sync_success') {
        return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">同步成功</span>;
      }
      if (action === 'sheet_sync_failed') {
        return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">同步失敗</span>;
      }
    }

    const config = typeMap[type] || { label: type || '操作', style: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${config.style}`}>
        {config.label}
      </span>
    );
  };

  const tabs = [
    { id: 'sync', label: '同步紀錄', count: syncPagination.total || syncLogs.length },
    { id: 'reminders', label: '提醒紀錄', count: reminderPagination.total || reminderLogs.length },
    { id: 'activity', label: '操作紀錄', count: activityPagination.total || activityLogs.length },
  ];

  // 分頁控制組件
  const Pagination = ({ currentPage, setPage, total, hasMore, pageSize: ps }) => {
    const totalPages = Math.ceil((total || 0) / ps) || 1;
    const showPagination = total > ps;

    return (
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600">
          共 {total || 0} 筆
          {showPagination && `，第 ${currentPage + 1} / ${totalPages} 頁`}
        </div>
        {showPagination && (
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, currentPage - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一頁
            </button>
            <button
              onClick={() => setPage(currentPage + 1)}
              disabled={!hasMore && currentPage >= totalPages - 1}
              className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一頁
            </button>
          </div>
        )}
      </div>
    );
  };

  const isLoading = (activeTab === 'sync' && syncLoading) ||
                    (activeTab === 'reminders' && reminderLoading) ||
                    (activeTab === 'activity' && activityLoading);

  return (
    <div className="p-6">
      {/* Tab 切換 */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium text-sm transition ${
              activeTab === tab.id
                ? 'border-b-2 border-purple-500 text-purple-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4" />
          <p className="text-gray-500">載入中...</p>
        </div>
      )}

      {/* 同步紀錄表格 */}
      {!isLoading && activeTab === 'sync' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">時間</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">狀態</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作者</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {syncLogs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      暫無同步紀錄
                    </td>
                  </tr>
                ) : (
                  syncLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(log.timestamp || log.synced_at || log.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getStatusBadge(log.status, log.success)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                        <div className="truncate" title={log.description}>
                          {log.description || `${log.details?.form_name || '未知表單'} 同步`}
                        </div>
                        {log.details && (
                          <details className="mt-1">
                            <summary className="text-xs text-purple-600 cursor-pointer hover:text-purple-800">
                              查看詳情
                            </summary>
                            <div className="mt-1 p-2 bg-gray-100 rounded text-xs">
                              {log.success || log.status === 'success' ? (
                                <span className="text-green-600">
                                  新增 {log.details?.created_count || 0} 筆，更新 {log.details?.updated_count || 0} 筆
                                  {log.details?.errors_count > 0 && `，${log.details.errors_count} 筆錯誤`}
                                </span>
                              ) : log.status === 'rejected' ? (
                                <span className="text-orange-600">
                                  {log.details?.vendor_name || log.details?.vendor_id || '未知廠商'}：
                                  {log.details?.detail || '連結驗證未通過'}
                                  {log.details?.enforced === false && '（觀察模式，已放行）'}
                                </span>
                              ) : (
                                <span className="text-red-600">
                                  {log.details?.error || log.error || '未知錯誤'}
                                </span>
                              )}
                            </div>
                          </details>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">
                          {log.performed_by_name || (log.performed_by === 'user' ? '手動觸發' : '系統自動同步')}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={syncPage}
            setPage={setSyncPage}
            total={syncPagination.total}
            hasMore={syncPagination.hasMore}
            pageSize={pageSize}
          />
        </div>
      )}

      {/* 提醒紀錄表格 */}
      {!isLoading && activeTab === 'reminders' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">時間</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">廠商 / 收件人</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">表單</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">類型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">狀態</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reminderLogs.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                      暫無提醒紀錄
                    </td>
                  </tr>
                ) : (
                  reminderLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(log.sent_at || log.checked_at || log.failed_at || log.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{log.vendor_name}</div>
                        <div className="text-gray-500 text-xs">{log.recipient_email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {log.form_name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getReminderTypeBadge(log.reminder_type, log.days_until_deadline)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getStatusBadge(log.status)}
                        {log.skip_reason && (
                          <div className="text-xs text-gray-500 mt-1">{log.skip_reason}</div>
                        )}
                        {log.error && (
                          <div className="text-xs text-red-600 mt-1" title={log.error}>
                            {log.error.substring(0, 30)}...
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={reminderPage}
            setPage={setReminderPage}
            total={reminderPagination.total}
            hasMore={reminderPagination.hasMore}
            pageSize={pageSize}
          />
        </div>
      )}

      {/* 操作紀錄表格 */}
      {!isLoading && activeTab === 'activity' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">時間</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">類型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作者</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activityLogs.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      暫無操作紀錄
                    </td>
                  </tr>
                ) : (
                  activityLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(log.performed_at || log.created_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getActivityTypeBadge(log.activity_type, log.action)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                        <div className="truncate" title={log.description}>
                          {log.description}
                        </div>
                        {log.details && (
                          <details className="mt-1">
                            <summary className="text-xs text-purple-600 cursor-pointer hover:text-purple-800">
                              查看詳情
                            </summary>
                            <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-w-sm">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{log.performed_by_name || '系統'}</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            currentPage={activityPage}
            setPage={setActivityPage}
            total={activityPagination.total}
            hasMore={activityPagination.has_more}
            pageSize={pageSize}
          />
        </div>
      )}
    </div>
  );
}
